// Make ElFormItem validation errors reach assistive technology.
//
// Element Plus 2.x renders a field error as a bare `.el-form-item__error` div:
// no role, no id, and the control it belongs to gets neither `aria-invalid`
// nor `aria-describedby`. A screen-reader user submits, hears nothing, and
// the field still reads as valid (WCAG 3.3.1, 4.1.3). EP exposes no hook for
// it and the v3 forms set `:error` on nearly every ElFormItem, so this runs
// once, app-wide, as error nodes come and go:
//
//   - the error div gets an id and role="alert", so it is announced when it
//     appears (and again if its text changes);
//   - each text control in the item gets aria-invalid="true" and an
//     aria-describedby entry pointing at the error, both removed when the
//     error clears.
//
// Only childList is observed: EP inserts and removes the error div (inside a
// <transition>) rather than toggling a class, and the attributes written here
// are not observed, so the observer never feeds itself.

const ERROR = 'el-form-item__error';
const CONTROLS = 'input:not([type="hidden"]), textarea';
const ID_ATTR = 'data-a11y-error-id';

let seq = 0;

// A nested ElFormItem's controls and error belong to that item, not this one.
function ownOf<T extends Element>(item: Element, selector: string): T[] {
  return [...item.querySelectorAll<T>(selector)].filter((c) => c.closest('.el-form-item') === item);
}

function withoutId(describedBy: string | null, id: string): string {
  return (describedBy ?? '')
    .split(/\s+/)
    .filter((t) => t && t !== id)
    .join(' ');
}

function sync(item: Element): void {
  const [error] = ownOf<HTMLElement>(item, `.${ERROR}`);
  const prev = item.getAttribute(ID_ATTR);

  if (error) {
    if (!error.id) error.id = `cd-form-error-${++seq}`;
    error.setAttribute('role', 'alert');
    for (const c of ownOf<HTMLElement>(item, CONTROLS)) {
      c.setAttribute('aria-invalid', 'true');
      const rest = withoutId(c.getAttribute('aria-describedby'), prev ?? error.id);
      c.setAttribute('aria-describedby', rest ? `${rest} ${error.id}` : error.id);
    }
    item.setAttribute(ID_ATTR, error.id);
  } else if (prev) {
    for (const c of ownOf<HTMLElement>(item, CONTROLS)) {
      c.removeAttribute('aria-invalid');
      const rest = withoutId(c.getAttribute('aria-describedby'), prev);
      if (rest) c.setAttribute('aria-describedby', rest);
      else c.removeAttribute('aria-describedby');
    }
    item.removeAttribute(ID_ATTR);
  }
}

function touchesError(node: Node): boolean {
  return (
    node instanceof Element && (node.classList.contains(ERROR) || !!node.querySelector(`.${ERROR}`))
  );
}

export function installFormErrorA11y(root: Node = document.body): () => void {
  const observer = new MutationObserver((records) => {
    const items = new Set<Element>();
    for (const r of records) {
      if (![...r.addedNodes, ...r.removedNodes].some(touchesError)) continue;
      // A removed node is already detached; the record's target is not.
      const host = r.target instanceof Element ? r.target.closest('.el-form-item') : null;
      if (host) items.add(host);
      // A whole form mounting with errors already showing.
      for (const n of r.addedNodes) {
        if (!(n instanceof Element)) continue;
        for (const e of n.querySelectorAll(`.${ERROR}`)) {
          const item = e.closest('.el-form-item');
          if (item) items.add(item);
        }
      }
    }
    for (const item of items) sync(item);
  });
  observer.observe(root, { childList: true, subtree: true });
  return () => observer.disconnect();
}
